import {useEffect, useRef, useState} from 'react';
import {Button, Dialog, Field, Input, Portal, Switch} from '@chakra-ui/react';
import {DialogCloseButton} from './ui/dialog-close-button';
import {PlazmaCopiesOption} from './PlazmaCopiesOption';
import {withNoexportPrefix} from '../domain/naming';

interface RenameModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentName: string;
    onSubmit: (newName: string, alsoRenamePlazmaCopy: boolean) => void;
    isSubmitting?: boolean;
    title?: string;
    submitLabel?: string;
    /** The file being renamed also has a plazma copy — offers renaming it too. */
    plazmaStarred?: boolean;
    /** Offers a switch that saves the name with the noexport prefix (for new folders). */
    noexportOption?: boolean;
}

/** A single-field name prompt — doubles as the "rename" dialog and, with an empty currentName, a "new folder" name prompt. */
export function RenameModal({
    isOpen,
    onClose,
    currentName,
    onSubmit,
    isSubmitting,
    title = 'Átnevezés',
    submitLabel = 'Átnevezés',
    plazmaStarred = false,
    noexportOption = false,
}: RenameModalProps) {
    const [name, setName] = useState(currentName);
    const [renamePlazmaCopy, setRenamePlazmaCopy] = useState(false);
    const [noexport, setNoexport] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) setName(currentName);
    }, [isOpen, currentName]);

    // Default to leaving the plazma copy alone (and to a normal folder) every time the dialog opens.
    useEffect(() => {
        if (isOpen) {
            setRenamePlazmaCopy(false);
            setNoexport(false);
        }
    }, [isOpen]);

    function handleSubmit() {
        const trimmed = name.trim();
        const newName = noexportOption && noexport ? withNoexportPrefix(trimmed) : trimmed;
        if (!trimmed || newName === currentName) {
            onClose();
            return;
        }
        onSubmit(newName, plazmaStarred && renamePlazmaCopy);
    }

    return <Dialog.Root
        open={isOpen}
        onOpenChange={(e) => {
            if (!e.open) onClose();
        }}
        initialFocusEl={() => inputRef.current}
    >
        <Portal>
            <Dialog.Backdrop/>
            <Dialog.Positioner>
                <Dialog.Content>
                    <Dialog.Header><Dialog.Title>{title}</Dialog.Title></Dialog.Header>
                    <DialogCloseButton/>
                    <Dialog.Body>
                        <Field.Root>
                            <Field.Label>Név</Field.Label>
                            <Input
                                ref={inputRef}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSubmit();
                                }}
                            />
                        </Field.Root>
                        {noexportOption &&
                            <Switch.Root
                                mt={4}
                                gap={2}
                                colorPalette="blue"
                                checked={noexport}
                                onCheckedChange={(e) => setNoexport(e.checked)}
                            >
                                <Switch.HiddenInput/>
                                <Switch.Control/>
                                <Switch.Label fontWeight="normal">
                                    Nem exportálandó mappa (NOEXPORT)
                                </Switch.Label>
                            </Switch.Root>
                        }
                        {plazmaStarred &&
                            <PlazmaCopiesOption
                                fileCount={1}
                                starredCount={1}
                                action="rename"
                                checked={renamePlazmaCopy}
                                onChange={setRenamePlazmaCopy}
                            />
                        }
                    </Dialog.Body>
                    <Dialog.Footer gap={0}>
                        <Button variant="ghost" mr={3} onClick={onClose}>Mégse</Button>
                        <Button colorPalette="blue" onClick={handleSubmit} loading={isSubmitting} disabled={!name.trim()}>
                            {submitLabel}
                        </Button>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog.Positioner>
        </Portal>
    </Dialog.Root>;
}
